// ChatMessages Component - Mesaj render bileşeni
import React, { memo, useCallback, useMemo } from "react";
import { Bot, User, Download, Copy, Share2, ThumbsUp, ThumbsDown, Volume2, VolumeX, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n";
import { toast } from "sonner";
import axios from "axios";
import { MarkdownMessage } from "./MarkdownMessage";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Personality Icons
const PERSONALITY_ICONS = {
  default: Bot,
  creative: Bot,
  analytical: Bot,
  friendly: Bot,
  professional: Bot
};

// Memoized Message Item Component
const MessageItem = memo(function MessageItem({
  message,
  isUser,
  isImage,
  isUploadedImage,
  voiceEnabled,
  isSpeaking,
  speakText,
  stopSpeaking,
  showConfirm,
  setMessages,
  activeConversation,
  messages,
  selectedAssistant,
  selectedCustomAssistant,
  t
}) {
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(message.content);
    toast.success(t('copied'));
  }, [message.content, t]);

  const handleShare = useCallback(() => {
    if (navigator.share) {
      navigator.share({ text: message.content });
    } else {
      navigator.clipboard.writeText(message.content);
      toast.success(t('copied'));
    }
  }, [message.content, t]);

  const handleFeedback = useCallback(async (feedbackType) => {
    try {
      await axios.post(`${API}/feedback/quick`, {
        message_id: message.id,
        conversation_id: activeConversation?.id,
        feedback_type: feedbackType,
        user_query: messages[messages.indexOf(message) - 1]?.content || "",
        ai_response: message.content,
        assistant_type: selectedCustomAssistant?.name || selectedAssistant
      });
      toast.success(t('feedback_saved'));
    } catch (e) { console.error(e); }
  }, [message, activeConversation, messages, selectedAssistant, selectedCustomAssistant, t]);

  const handleDelete = useCallback(() => {
    showConfirm({
      title: t('delete_message_title'),
      message: t('delete_message_confirm'),
      confirmText: t('yes_delete'),
      cancelText: t('cancel'),
      variant: "danger",
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/chat/messages/${message.id}`);
          setMessages(prev => prev.filter(m => m.id !== message.id));
          toast.success(t('deleted_successfully'));
        } catch (e) { 
          console.error(e); 
          toast.error(t('error_occurred'));
        }
      }
    });
  }, [message.id, showConfirm, setMessages, t]);

  return (
    <div className={`message-bubble ${isUser ? "user-message" : "ai-message"}`}>
      <div className={`message-icon ${isUser ? "bg-gradient-to-br from-green-500 to-emerald-600" : "bg-gradient-to-br from-blue-500 to-purple-600"}`}>
        {isUser ? <User size={18} /> : <Bot size={18} />}
      </div>
      <div className="message-content">
        {isImage ? (
          <div className="generated-image-container">
            <img src={message.content} alt="Generated" className="generated-image" />
            <a href={message.content} download="kaan-ai-image.png" className="download-button">
              <Download size={16} /> İndir
            </a>
          </div>
        ) : isUploadedImage ? (
          <img src={message.content} alt="Uploaded" className="uploaded-image" />
        ) : (
          <MarkdownMessage content={message.content} isUser={isUser} />
        )}
        
        {/* Message Actions */}
        <div className="flex items-center gap-1 mt-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 px-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
            onClick={handleCopy}
            title={t('copy')}
            data-testid={`copy-btn-${message.id}`}
          >
            <Copy size={14} />
          </Button>
          
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 px-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
            onClick={handleShare}
            title={t('share')}
            data-testid={`share-btn-${message.id}`}
          >
            <Share2 size={14} />
          </Button>
          
          {!isUser && (
            <>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 px-2 text-[var(--text-muted)] hover:text-green-400 hover:bg-green-500/10"
                onClick={() => handleFeedback("positive")}
                title={t('like') || 'Beğen'}
                data-testid={`like-btn-${message.id}`}
              >
                <ThumbsUp size={14} />
              </Button>
              
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-7 px-2 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10"
                onClick={() => handleFeedback("negative")}
                title={t('dislike') || 'Beğenme'}
                data-testid={`dislike-btn-${message.id}`}
              >
                <ThumbsDown size={14} />
              </Button>
            </>
          )}
          
          {!isUser && !isImage && !isUploadedImage && voiceEnabled && (
            <Button 
              variant="ghost" 
              size="sm" 
              className={`h-7 px-2 ${isSpeaking ? "text-red-400" : "text-[var(--text-muted)] hover:text-blue-400 hover:bg-blue-500/10"}`}
              onClick={() => isSpeaking ? stopSpeaking() : speakText(message.content)}
              title={isSpeaking ? t('stop') : t('listen') || 'Dinle'}
              data-testid={`voice-btn-${message.id}`}
            >
              {isSpeaking ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </Button>
          )}
          
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 px-2 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10"
            onClick={handleDelete}
            title={t('delete')}
            data-testid={`delete-btn-${message.id}`}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
});

export function ChatMessages({
  messages,
  activeConversation,
  selectedAssistant,
  selectedCustomAssistant,
  voiceEnabled,
  isSpeaking,
  speakText,
  stopSpeaking,
  setMessages,
  showConfirm,
  streamingText,
  isLoading,
  messagesEndRef
}) {
  const { t } = useLanguage();
  const PersonalityIcon = PERSONALITY_ICONS[activeConversation?.personality || "default"] || Bot;

  const downloadImage = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'kaan-gorsel.png';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderMessage = (message) => {
    const isUser = message.role === "user";
    const isImage = message.type === "image";
    const isUploadedImage = message.type === "uploaded_image";

    return (
      <div key={message.id} className="message group" data-testid={`message-${message.id}`}>
        <div className={`message-avatar ${isUser ? "user" : "assistant"}`}>
          {isUser ? <User size={18} /> : <PersonalityIcon size={18} />}
        </div>
        <div className={`message-content ${isUser ? "user" : "assistant"}`}>
          {isImage ? (
            <div className="relative group">
              <img src={message.content} alt="Generated" className="image-preview" data-testid="generated-image" />
              <Button 
                variant="ghost" 
                size="sm" 
                className="absolute bottom-2 right-2 bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => downloadImage(message.content, `kaan-gorsel-${message.id}.png`)}
              >
                <Download size={14} className="mr-1" /> İndir
              </Button>
            </div>
          ) : isUploadedImage && message.content.startsWith("data:image") ? (
            <img src={message.content} alt="Uploaded" className="image-preview max-w-xs rounded-lg" />
          ) : message.image_preview ? (
            <div>
              <img src={message.image_preview} alt="Uploaded" className="image-preview max-w-xs rounded-lg mb-2" />
              <MarkdownMessage content={message.content} isUser={isUser} />
            </div>
          ) : (
            <MarkdownMessage content={message.content} isUser={isUser} />
          )}
          
          {/* Message Actions */}
          <div className="flex items-center gap-1 mt-2">
            {/* Kopyala */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 px-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
              onClick={() => {
                navigator.clipboard.writeText(message.content);
                toast.success(t('copied'));
              }}
              title={t('copy')}
              data-testid={`copy-btn-${message.id}`}
            >
              <Copy size={14} />
            </Button>
            
            {/* Paylaş */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 px-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
              onClick={() => {
                if (navigator.share) {
                  navigator.share({ text: message.content });
                } else {
                  navigator.clipboard.writeText(message.content);
                  toast.success(t('copied'));
                }
              }}
              title={t('share')}
              data-testid={`share-btn-${message.id}`}
            >
              <Share2 size={14} />
            </Button>
            
            {/* Beğen - Sadece asistan mesajları için */}
            {!isUser && (
              <>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 px-2 text-[var(--text-muted)] hover:text-green-400 hover:bg-green-500/10"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/feedback/quick`, {
                        message_id: message.id,
                        conversation_id: activeConversation?.id,
                        feedback_type: "positive",
                        user_query: messages[messages.indexOf(message) - 1]?.content || "",
                        ai_response: message.content,
                        assistant_type: selectedCustomAssistant?.name || selectedAssistant
                      });
                      toast.success(t('feedback_saved'));
                    } catch (e) { console.error(e); }
                  }}
                  title={t('like') || 'Beğen'}
                  data-testid={`like-btn-${message.id}`}
                >
                  <ThumbsUp size={14} />
                </Button>
                
                {/* Beğenme */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 px-2 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/feedback/quick`, {
                        message_id: message.id,
                        conversation_id: activeConversation?.id,
                        feedback_type: "negative",
                        user_query: messages[messages.indexOf(message) - 1]?.content || "",
                        ai_response: message.content,
                        assistant_type: selectedCustomAssistant?.name || selectedAssistant
                      });
                      toast.success(t('feedback_saved'));
                    } catch (e) { console.error(e); }
                  }}
                  title={t('dislike') || 'Beğenme'}
                  data-testid={`dislike-btn-${message.id}`}
                >
                  <ThumbsDown size={14} />
                </Button>
              </>
            )}
            
            {/* Sesli Oku - Sadece asistan mesajları için */}
            {!isUser && !isImage && !isUploadedImage && voiceEnabled && (
              <Button 
                variant="ghost" 
                size="sm" 
                className={`h-7 px-2 ${isSpeaking ? "text-red-400" : "text-[var(--text-muted)] hover:text-blue-400 hover:bg-blue-500/10"}`}
                onClick={() => isSpeaking ? stopSpeaking() : speakText(message.content)}
                title={isSpeaking ? t('stop') : t('listen') || 'Dinle'}
                data-testid={`voice-btn-${message.id}`}
              >
                {isSpeaking ? <VolumeX size={14} /> : <Volume2 size={14} />}
              </Button>
            )}
            
            {/* Sil */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 px-2 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10"
              onClick={() => {
                showConfirm({
                  title: t('delete_message_title'),
                  message: t('delete_message_confirm'),
                  confirmText: t('yes_delete'),
                  cancelText: t('cancel'),
                  variant: "danger",
                  onConfirm: async () => {
                    try {
                      await axios.delete(`${API}/chat/messages/${message.id}`);
                      setMessages(prev => prev.filter(m => m.id !== message.id));
                      toast.success(t('deleted_successfully'));
                    } catch (e) { 
                      console.error(e); 
                      toast.error(t('error_occurred'));
                    }
                  }
                });
              }}
              title={t('delete')}
              data-testid={`delete-btn-${message.id}`}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {messages.map(renderMessage)}
      {streamingText && (
        <div className="message">
          <div className="message-avatar assistant">
            <PersonalityIcon size={18} />
          </div>
          <div className="message-content assistant">
            <div className="whitespace-pre-wrap">{streamingText}<span className="animate-pulse">▊</span></div>
          </div>
        </div>
      )}
      {isLoading && !streamingText && (
        <div className="message">
          <div className="message-avatar assistant"><Bot size={18} /></div>
          <div className="message-content assistant">
            <div className="loading-dots">
              <div className="loading-dot" style={{ animationDelay: "0s" }} />
              <div className="loading-dot" style={{ animationDelay: "0.1s" }} />
              <div className="loading-dot" style={{ animationDelay: "0.2s" }} />
            </div>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </>
  );
}

// Memoize the entire component for performance
export default memo(ChatMessages);
